import firebase from "firebase/app";
import "firebase/functions";
import firestore from "src/apiService/firestore";

import { isFloatEqual } from "src/utils/number";

import type { Reconciliation } from "src/interfaces/reconciliation.interface";
import type { BankAccountWithReconciliations } from "src/pages/Reconciliation/types";

type UpdateReconciliationPayload = Partial<
  Pick<Reconciliation, "date" | "balance" | "transactions">
>;

export interface ReconciliationContextRow {
  id: string;
  collection: "sale" | "transaction";
  collection_id: string;
  date: string;
  description: string;
  charge_description: string;
  amount: number;
  matched: boolean;
}

export interface ReconciliationContextPayload {
  id: string;
  open: boolean;
  balance: number;
  startingBalance: number;
  bank_account_id: string;
  transactions: { type: "sale" | "transaction"; id: string }[];
  rows: ReconciliationContextRow[];
  affectedReconciliations?: Array<{
    id: string;
    open: boolean;
    balance: number;
    bank_account_id: string;
  }>;
}

export type UpdateReconciliationResponse = ReconciliationContextPayload;

export interface CheckAndUpdateReconciliationsResponse {
  success: boolean;
  message?: string;
  bankAccounts: BankAccountWithReconciliations[];
}

export async function addReconciliation(
  bankAccountId: string,
  balance: number,
  date: string
) {
  const f = firebase
    .functions()
    .httpsCallable("bankReconciliations-createReconciliation");
  const result = await f({
    bankAccountId,
    balance,
    date,
  });
  return result.data;
}

export async function updateReconciliation(
  id: string,
  update: UpdateReconciliationPayload
) {
  const f = firebase
    .functions()
    .httpsCallable("bankReconciliations-updateReconciliation");
  const payload: Record<string, unknown> = {
    id,
  };
  if ("balance" in update) payload.balance = update.balance;
  if ("transactions" in update) payload.transactions = update.transactions;
  if ("date" in update && update.date) payload.date = update.date;
  const result = await f(payload);
  return result.data as UpdateReconciliationResponse;
}

export async function getReconciliationContext(reconciliationId: string) {
  const f = firebase
    .functions()
    .httpsCallable("bankReconciliations-getReconciliationContext");
  const result = await f({
    reconciliationId,
  });
  return result.data as ReconciliationContextPayload;
}

export async function updateReconciliciationOpenIfNeeded(
  id: string,
  balance: number
) {
  const db = firestore();
  return await db.runTransaction(async (t) => {
    const ref = db.collection("Reconciliations").doc(id);
    const doc = await t.get(ref);
    const data = doc.data();
    const dataBalance =
      data?.balance != null && Number.isFinite(data.balance)
        ? Number(data.balance)
        : undefined;
    if (dataBalance !== undefined && isFloatEqual(dataBalance, balance)) {
      if (data?.open) await t.update(ref, { open: false });
      return false;
    } else if (!data?.open) {
      await t.update(ref, { open: true });
    }
    return true;
  });
}

export async function checkAndUpdateReconciliations() {
  const f = firebase
    .functions()
    .httpsCallable("bankReconciliations-checkAndUpdateReconciliations");
  const result = await f({});
  return result.data as CheckAndUpdateReconciliationsResponse;
}
